import { getStateInvariantViolations } from "../invariants/game-state-invariants";
import type {
  ActionValidationFailure,
  ActionValidationResult,
  GameAction,
  GameState,
} from "../types";
import { getOpponentPlayerId } from "../types";
import { validateDeployArmyAction } from "./validate-army-deployment";
import {
  validateDisbandArmyAction,
  validateDisbandCommanderAction,
} from "./validate-disband-action";
import {
  validateDeployCommanderAction,
  validateReplaceCommanderAction,
} from "./validate-commander-action";
import { validateDevelopLandAction, validateFoundLandAction } from "./validate-land-action";
import { validateAdvancePhaseAction } from "./validate-phase-action";
import { validateDiscardArmyForUpkeepAction } from "./validate-supply-action";
import { getAttackKind, isActiveLandForPlayer } from "../selectors/formation";
import { validateEndTurnAction } from "./validate-end-turn";
import { validatePlayEventAction } from "./validate-event-action";

function invalid(
  code: ActionValidationFailure["code"],
  message: string,
  path: string | null = null,
  details: ActionValidationFailure["details"] = {},
): ActionValidationResult {
  return {
    valid: false,
    failure: { code, message, path, details },
  };
}

export function validateAction(
  state: Readonly<GameState>,
  action: GameAction,
): ActionValidationResult {
  const invariantViolation = getStateInvariantViolations(state)[0];
  if (invariantViolation !== undefined) {
    return invalid("INVALID_STATE", invariantViolation.message, invariantViolation.path, {
      invariantCode: invariantViolation.code,
    });
  }

  if (state.status.type === "finished" || state.phase === "finished") {
    return invalid(
      "MATCH_FINISHED",
      "No actions are legal after the match has finished.",
      "status",
    );
  }

  if (action.playerId !== state.activePlayerId) {
    return invalid("WRONG_PLAYER", "Only the active player may act.", "playerId", {
      activePlayerId: state.activePlayerId,
      actionPlayerId: action.playerId,
    });
  }

  if (action.type === "deferred") {
    return invalid(
      "ACTION_NOT_IMPLEMENTED",
      `The ${action.actionKind} rules are not implemented in this engine slice.`,
      "type",
      { actionKind: action.actionKind },
    );
  }

  if (action.type === "advance-phase") {
    return validateAdvancePhaseAction(state);
  }

  if (action.type === "found-land") {
    return validateFoundLandAction(state, action);
  }

  if (action.type === "develop-land") {
    return validateDevelopLandAction(state, action);
  }

  if (action.type === "discard-army-for-upkeep") {
    return validateDiscardArmyForUpkeepAction(state, action);
  }

  if (action.type === "disband-army") {
    return validateDisbandArmyAction(state, action);
  }

  if (action.type === "disband-commander") {
    return validateDisbandCommanderAction(state, action);
  }

  if (action.type === "deploy-commander") {
    return validateDeployCommanderAction(state, action);
  }

  if (action.type === "deploy-army") {
    return validateDeployArmyAction(state, action);
  }

  if (action.type === "replace-commander") {
    return validateReplaceCommanderAction(state, action);
  }

  if (action.type === "play-event") {
    return validatePlayEventAction(state, action);
  }

  if (action.type === "end-turn") {
    return validateEndTurnAction(state);
  }

  if (state.phase !== "battle" && !(state.schemaVersion === 5 && state.phase === "action-window")) {
    return invalid(
      "WRONG_PHASE",
      "A normal attack is legal only during the Battle Phase.",
      "phase",
      {
        currentPhase: state.phase,
        requiredPhase: state.schemaVersion === 5 ? "action-window" : "battle",
      },
    );
  }

  const lane = state.lanes[action.laneId];
  if (lane.result !== "active") {
    return invalid(
      "LANE_NOT_ACTIVE",
      "A resolved lane no longer participates in the match.",
      `lanes.${action.laneId}.result`,
      { laneResult: lane.result },
    );
  }

  const attackingSide = lane.players[action.playerId];
  if (!isActiveLandForPlayer(state, action.playerId, action.laneId)) {
    return invalid(
      "LANE_NOT_ACTIVE",
      "The attacking side of this lane is not active.",
      `lanes.${action.laneId}.players.${action.playerId}.status`,
      { laneSideStatus: attackingSide.status },
    );
  }

  if (attackingSide.land === null) {
    return invalid(
      "MISSING_ATTACKING_LAND",
      "A normal attack requires an active Land in the lane.",
      `lanes.${action.laneId}.players.${action.playerId}.land`,
    );
  }

  const commander = attackingSide.commander;
  if (commander === null) {
    return invalid(
      "MISSING_ATTACKING_COMMANDER",
      "A lane without a Commander cannot make a normal attack.",
      `lanes.${action.laneId}.players.${action.playerId}.commander`,
    );
  }

  if (commander.readiness === "spent") {
    return invalid(
      "COMMANDER_SPENT",
      "A Spent Commander cannot attack again this turn.",
      `lanes.${action.laneId}.players.${action.playerId}.commander.readiness`,
    );
  }

  if (state.players[action.playerId].actionsUsed.attacksMadeFromLanes.includes(action.laneId)) {
    return invalid(
      "COMMANDER_SPENT",
      "Each source formation may attack only once per Battle Phase.",
      `players.${action.playerId}.actionsUsed.attacksMadeFromLanes`,
      { sourceLaneId: action.laneId },
    );
  }

  if (commander.deployedThisTurn && !commander.mayAttackOnDeploymentTurn) {
    return invalid(
      "COMMANDER_DEPLOYED_THIS_TURN",
      "A newly deployed Commander cannot attack this turn.",
      `lanes.${action.laneId}.players.${action.playerId}.commander.deployedThisTurn`,
    );
  }

  const defendingPlayerId = getOpponentPlayerId(action.playerId);
  const targetLaneId = action.targetLaneId ?? action.laneId;
  if (state.schemaVersion !== 4 && state.schemaVersion !== 5 && targetLaneId !== action.laneId) {
    return invalid(
      "NO_TARGET_LAND",
      "Historical rules versions support only an opposite-lane direct attack.",
      "targetLaneId",
      { sourceLaneId: action.laneId, targetLaneId },
    );
  }
  const defendingSide = state.lanes[targetLaneId].players[defendingPlayerId];
  if (!isActiveLandForPlayer(state, defendingPlayerId, targetLaneId)) {
    return invalid(
      "NO_TARGET_LAND",
      "An attack requires an opposing Active Land target.",
      `lanes.${targetLaneId}.players.${defendingPlayerId}.land`,
    );
  }

  const attackKind = getAttackKind(state, action.playerId, action.laneId, targetLaneId);
  if (attackKind === null) {
    return invalid(
      "NO_TARGET_LAND",
      "An opposite Active Land must be attacked; Flank requires no opposite Active Land.",
      "targetLaneId",
      { sourceLaneId: action.laneId, targetLaneId },
    );
  }

  if (
    attackingSide.conspiracyCardInstanceId !== null ||
    defendingSide.conspiracyCardInstanceId !== null
  ) {
    return invalid(
      "ACTION_NOT_IMPLEMENTED",
      "Attacks involving a Conspiracy require the deferred combat-reaction effect pipeline.",
      `lanes.${targetLaneId}`,
      { actionKind: "resolve-card-effect" },
    );
  }

  return { valid: true };
}
